import React, { useState } from 'react'
import { CheckCircle, XCircle, BookOpen, Calculator, AlertCircle, Maximize2, Minimize2 } from 'lucide-react'
import KaTeXRenderer from '../common/KaTeXRenderer'
import AIButton from '../ai/AIButton'

interface ShortAnswerSolutionDisplayProps {
  question: any
  userAnswer: any
  analysis: any
}

const ShortAnswerSolutionDisplay: React.FC<ShortAnswerSolutionDisplayProps> = ({
  question,
  userAnswer,
  analysis
}) => {
  const [imageExpanded, setImageExpanded] = useState(false)
  const hasAnswer = userAnswer !== null && userAnswer !== undefined && userAnswer !== ''
  const correctAnswer = analysis.correctAnswer || question.dap_an_dung

  // Try to parse answers as numbers for comparison
  const parseAnswer = (answer: any) => {
    if (answer === null || answer === undefined) return null
    const str = String(answer).trim().replace(/,/g, '.')
    const num = parseFloat(str)
    return isNaN(num) ? str : num
  }

  const parsedUserAnswer = parseAnswer(userAnswer)
  const parsedCorrectAnswer = parseAnswer(correctAnswer)

  // Check if answers are numeric
  const isNumeric = typeof parsedUserAnswer === 'number' && typeof parsedCorrectAnswer === 'number'

  return (
    <div className="mt-2 space-y-2">
      {/* Question Content */}
      <div className="bg-gray-50 rounded p-3 relative group">
        <div className="flex items-center gap-2 mb-1.5">
          <Calculator className="w-4 h-4 text-blue-600" />
          <span className="font-semibold text-gray-800 text-sm">Câu hỏi tự luận:</span>
        </div>
        <div className="text-sm text-gray-700">
          <KaTeXRenderer content={question.cau_hoi || question.cauHoi || ''} />
        </div>
        
        {/* AI Button - Hiển thị ở chế độ xem lại */}
        <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100 transition-opacity">
          <AIButton
            content={question}
            contentType="quiz"
            size="sm"
            variant="outline"
            title="Giải thích câu hỏi tính toán"
            className="bg-white border-blue-300 text-blue-600 hover:bg-blue-50"
            questionId={question.id}
            useDatabase={true}
          />
        </div>
        
        {/* Question Image */}
        {(question.hinh_anh || question.questionImage) && (
          <div className="mt-2 w-full overflow-hidden">
            <img
              src={question.hinh_anh || question.questionImage}
              alt="Hình ảnh câu hỏi"
              className="w-full h-auto max-h-[350px] md:max-h-[400px] rounded border border-gray-200 mx-auto block object-contain"
              style={{ maxWidth: '100%', width: 'auto' }}
              onError={(e) => {
                const target = e.currentTarget as HTMLImageElement
                target.style.display = 'none'
              }}
            />
          </div>
        )}
      </div>

      {/* Answer Comparison */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
        {/* User Answer */}
        <div className={`border rounded p-3 ${
          hasAnswer 
            ? analysis.isCorrect 
              ? 'border-green-200 bg-green-50' 
              : 'border-red-200 bg-red-50'
            : 'border-gray-200 bg-gray-50'
        }`}>
          <div className="flex items-center gap-2 mb-1.5">
            <span className="font-medium text-gray-800 text-sm">Bạn trả lời:</span>
          </div>
          <div className={`text-sm font-mono bg-white p-2 rounded border ${
            hasAnswer ? 'text-gray-800' : 'text-gray-400'
          }`}>
            {hasAnswer ? (
              <KaTeXRenderer content={String(userAnswer)} />
            ) : (
              <span className="italic">Chưa trả lời</span>
            )}
          </div>
        </div>

        {/* Correct Answer */}
        <div className="border-green-200 bg-green-50 border rounded p-3">
          <div className="flex items-center gap-2 mb-1.5">
            <CheckCircle className="w-4 h-4 text-green-600" />
            <span className="font-medium text-green-800 text-sm">Đáp án đúng:</span>
          </div>
          <div className="text-sm font-mono bg-white p-2 rounded border text-gray-800">
            <KaTeXRenderer content={String(correctAnswer || 'Không có đáp án')} />
          </div>
        </div>
      </div>

      {/* Detailed Solution */}
      {(analysis.explanation || question.loi_giai) && (
        <div className="bg-blue-50 border border-blue-200 rounded p-3">
          <div className="flex items-center gap-2 mb-2">
            <BookOpen className="w-4 h-4 text-blue-600" />
            <span className="font-semibold text-blue-800 text-sm">Lời giải chi tiết:</span>
          </div>
          <div className="text-sm text-blue-700">
            <KaTeXRenderer content={analysis.explanation || question.loi_giai} />
          </div>
          
          {/* Solution Image */}
          {(question.hinh_anh_giai_thich || question.solution_image_link) && (
            <div className="mt-2 w-full overflow-hidden">
              <img
                src={question.hinh_anh_giai_thich || question.solution_image_link}
                alt="Hình ảnh lời giải"
                className="w-full h-auto max-h-[350px] md:max-h-[400px] rounded border border-gray-200 mx-auto block object-contain"
                style={{ maxWidth: '100%', width: 'auto' }}
                onError={(e) => {
                  const target = e.currentTarget as HTMLImageElement
                  target.style.display = 'none'
                }}
              />
            </div>
          )}
        </div>
      )}
    </div>
  )
}

export default ShortAnswerSolutionDisplay